import React, { Fragment, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
  ToolbarSpacer,
  Button,
  DropDownButton,
} from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { FormDropDownListById, FormInput } from "../../forms/form-components";
import {
  setCurentPage,
  setCurrentPageIsDirty,
  setCurrentPage,
} from "../../redux/slices/currentpageslice";
import {
  Form as KendoForm,
  Field,
  FormElement,
} from "@progress/kendo-react-form";
import { MyGrid } from "../../components/mygrid";
import { CircularGauge } from "@progress/kendo-react-gauges";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { formatNumber } from "../../lib/utils";
import {
  setPageValue,
  setFormValue,
  setRetentionPeriod,
  showDatabaseByMonthDialogOuter,
  setCleanups,
  operatorsLookup,
  clearAllCleanups,
  clearCleanupStrategy,
} from "../../redux/slices/dataconsumptionpageslice";
import { isEmptyObject } from "../../lib/utils";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const transformSize = (valueInMB) => {
  if (!valueInMB || isNaN(parseFloat(valueInMB))) return valueInMB;

  const format = (val, unit) => `${Math.round(val * 100) / 100} ${unit}`;

  if (valueInMB < 1024) {
    return format(valueInMB, "MB");
  } else if (valueInMB < 1024 * 1024) {
    return format(valueInMB / 1024, "GB");
  } else {
    return format(valueInMB / (1024 * 1024), "TB");
  }
};

const SizeDisplayer = (props) => {
  const { className, title, displayValue, currentTheme, tooltip, ...other } =
    props;

  return (
    <>
      <div title={tooltip} className={`size-displayer ${className}`}>
        <div className="title">{title}</div>
        <div className="value">{displayValue}</div>
      </div>
      <style>
        {`
                    .size-displayer
                    {
                        border: none;
                        border-left: 4px solid ${currentTheme.touchColor};
                        padding: 0px 36px 0px 4px;
                        display: flex;
                        flex-direction: column;
                    }
                    .size-displayer .title
                    {
                        font-size: 11px;
                        font-weight: 600;
                    }
                    .size-displayer .value
                    {
                        font-size: 24px;
                        font-weight: 600;
                    }
                    `}
      </style>
    </>
  );
};

export const DataConsumptionPageBody = (props) => {
  const { onSave, ...other } = props;

  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.dataConsumptionPage);
  const dispatch = useDispatch();
  const gaugeAlertValue = 75;

  const { t } = useTranslation();

  //Gauge
  const GaugeCenterRenderer = (value, color) => {
    return (
      <h3
        style={{
          color:
            value >= gaugeAlertValue
              ? currentTheme.error
              : currentTheme.touchColor,
        }}
      >
        {value}%<br />
        {t("dataconsumption.used")}
      </h3>
    );
  };

  let gaugeValue = pageState.databaseUsedPercentage;

  let gaugeValueFormatted = formatNumber(pageState.databaseUsedPercentage, 2);
  //Gauge

  //dialog
  const closeTitle =
    pageState.setRetentionPeriodDialogOpen || pageState.cleanupsDialogOpen
      ? t("dataconsumption.cancel")
      : t("dataconsumption.close");

  const onDialogClose = () => {
    dispatch(
      setPageValue({ id: "setRetentionPeriodDialogOpen", value: false }),
    );
    dispatch(setPageValue({ id: "databaseByMonthDialogOpen", value: false }));
    dispatch(setPageValue({ id: "datasetByMonthDialogOpen", value: false }));
    dispatch(setPageValue({ id: "cleanupsDialogOpen", value: false }));
    dispatch(setPageValue({ id: "selectedDatasetId", value: null }));
  };

  const formValidator = (formValues) => {
    let formErrors = {};

    if (
      pageState.databaseByMonthDialogOpen ||
      pageState.datasetByMonthDialogOpen
    )
      return undefined;

    if (pageState.setRetentionPeriodDialogOpen) {
      let retentionPeriodInDays = formValues["RetentionPeriodInDays"];
      if (isNaN(parseInt(retentionPeriodInDays)))
        formErrors = Object.assign(formErrors, {
          RetentionPeriodInDays: t("dataconsumption.retention"),
        });
      else if (
        parseInt(retentionPeriodInDays) < 0 ||
        parseInt(retentionPeriodInDays) > 3650
      )
        formErrors = Object.assign(formErrors, {
          RetentionPeriodInDays: t("dataconsumption.retention1"),
        });
    } else if (pageState.cleanupsDialogOpen) {
      let prop = "";

      let cleanups = formValues.Cleanups;
      for (let i in cleanups) {
        let c = cleanups[i];

        if (c.FieldId > 0) {
          if (c.OperatorId < 1 || c.OperatorId > 6) {
            prop = `Cleanups[${i}].OperatorId`;
            formErrors = Object.assign(formErrors, {
              [prop]: t("dataconsumption.invalid_op"),
            });
          }

          if (isNaN(parseInt(c.Value))) {
            prop = `Cleanups[${i}].Value`;
            formErrors = Object.assign(formErrors, {
              [prop]: t("dataconsumption.invalid_value"),
            });
          }

          if (
            isNaN(parseInt(c.RetentionPeriodInDays)) ||
            parseInt(c.RetentionPeriodInDays) < 1 ||
            parseInt(c.RetentionPeriodInDays) > 3650 ||
            c.RetentionPeriodInDays.toString().indexOf(".") >= 0
          ) {
            prop = `Cleanups[${i}].RetentionPeriodInDays`;
            formErrors = Object.assign(formErrors, {
              [prop]: t("dataconsumption.invalid_value"),
            });
          }
        }
      }
    }

    if (isEmptyObject(formErrors)) return undefined;

    return formErrors;
  };
  //dialog

  const schema = pageState.datasetsTable?.schema;

  const translatedSchema = schema?.map((col) => ({
    ...col,
    title: t(col.title),
  }));

  return (
    <>
      {pageState.processing && (
        <div className="page-loader">
          <Loader
            size={"large"}
            type="infinite-spinner"
            themeColor={"primary"}
          />
          {pageState.processingText && (
            <div style={{ fontSize: 13, color: currentTheme.primary }}>
              {pageState.processingText}
            </div>
          )}
        </div>
      )}
      {pageState.pageLoaded && (
        <div className="dataconsumption-body">
          <div className="page-header">
            <div>{t("dataconsumption.dataconsumption")}</div>
          </div>
          <div className="database">
            <div className="gauge">
              <CircularGauge
                style={{ width: "100%", height: "100%" }}
                scale={{
                  rangeSize: 24,
                  rangeLineCap: "butt",
                }}
                value={gaugeValueFormatted}
                centerRender={GaugeCenterRenderer}
                colors={[
                  {
                    from: 0,
                    to: gaugeValueFormatted,
                    color:
                      gaugeValue >= gaugeAlertValue
                        ? currentTheme.error
                        : currentTheme.touchColor,
                  },
                ]}
              />
            </div>
            <div className="summary">
              <div className="database-actions">
                <Button
                  disabled={pageState.processing}
                  style={{
                    padding: 8,
                    color: currentTheme.touchColor,
                    fontWeight: 500,
                  }}
                  onClick={(e) => {
                    dispatch(showDatabaseByMonthDialogOuter());
                  }}
                >
                  {t("dataconsumption.details")}
                </Button>
                {currentUser &&
                  currentUser.Subscription?.Extras &&
                  currentUser.Extras?.ManageConsumption && (
                    <Button
                      disabled={pageState.processing}
                      style={{
                        marginLeft: appearance.isRightToLeft ? 0 : 24,
                        marginRight: appearance.isRightToLeft ? 24 : 0,
                        padding: 8,
                        color: currentTheme.touchColor,
                        fontWeight: 500,
                      }}
                      onClick={props.onSave}
                    >
                      {t("dataconsumption.savechanges")}
                    </Button>
                  )}
              </div>
              <div className="storage-summary">
                <SizeDisplayer
                  tooltip={t("dataconsumption.maxstorage")}
                  currentTheme={currentTheme}
                  title={t("dataconsumption.maxstorage1")}
                  displayValue={transformSize(
                    pageState.databaseSubscriptionMaximumSizeInMB,
                  )}
                />
                <SizeDisplayer
                  tooltip={t("dataconsumption.allocatedspace")}
                  currentTheme={currentTheme}
                  title={t("dataconsumption.allocatedspace1")}
                  displayValue={transformSize(pageState.databaseTotalSizeInMB)}
                />
                <SizeDisplayer
                  tooltip={t("dataconsumption.usedspace")}
                  currentTheme={currentTheme}
                  title={t("dataconsumption.usedspace1")}
                  displayValue={transformSize(pageState.databaseUsedSizeInMB)}
                />
                <SizeDisplayer
                  currentTheme={currentTheme}
                  title={t("dataconsumption.remainspace")}
                  displayValue={transformSize(
                    pageState.databaseRemainingSizeInMB,
                  )}
                />
                <SizeDisplayer
                  tooltip={t("dataconsumption.importspace")}
                  currentTheme={currentTheme}
                  title={t("dataconsumption.importspace1")}
                  displayValue={transformSize(
                    pageState.databaseImportedSizeInMB,
                  )}
                />
                <SizeDisplayer
                  tooltip={t("dataconsumption.cleanedspace")}
                  className="cumulative-cleaned-space"
                  currentTheme={currentTheme}
                  title={t("dataconsumption.cleanedspace1")}
                  displayValue={transformSize(
                    pageState.databaseCleanedSizeInMB,
                  )}
                />
              </div>
            </div>
          </div>

          <div className="datasets">
            <MyGrid
              className="datasets-table"
              currentTheme={currentTheme}
              schema={translatedSchema}
              data={pageState.datasetsTable.data}
              isPageable={false}
              isFilterable={false}
              isSortable={true}
            />
          </div>
          {(pageState.setRetentionPeriodDialogOpen ||
            pageState.databaseByMonthDialogOpen ||
            pageState.datasetByMonthDialogOpen ||
            pageState.cleanupsDialogOpen) && (
            <Dialog
              className={`dataconsumption-dialog ${
                pageState.cleanupsDialogOpen
                  ? "dataconsumption-cleanups-dialog"
                  : ""
              }`}
              title={null}
            >
              <KendoForm
                validator={formValidator}
                key={pageState.formKey}
                initialValues={{ ...pageState.formInitialValues }}
                render={(formRenderProps) => {
                  return (
                    <FormElement
                      horizontal={false}
                      style={
                        {
                          // width: 250,
                        }
                      }
                    >
                      <div className="toolbar">
                        <Toolbar>
                          {pageState.setRetentionPeriodDialogOpen &&
                            pageState.canManageDataConsumption && (
                              <Fragment>
                                <ToolbarItem>
                                  <Button
                                    disabled={pageState.processing}
                                    style={{ color: currentTheme.primary }}
                                    icon="save"
                                    onClick={(e) => {
                                      if (
                                        formValidator(pageState.formValues) !==
                                        undefined
                                      )
                                        return;

                                      dispatch(setRetentionPeriod());
                                      dispatch(setCurrentPageIsDirty(true));
                                    }}
                                  >
                                    {t("dataconsumption.save")}
                                  </Button>
                                </ToolbarItem>
                                <ToolbarSeparator />
                              </Fragment>
                            )}
                          {pageState.cleanupsDialogOpen &&
                            pageState.canManageDataConsumption && (
                              <Fragment>
                                <ToolbarItem>
                                  <Button
                                    disabled={pageState.processing}
                                    style={{ color: currentTheme.primary }}
                                    icon="delete"
                                    onClick={(e) => {
                                      dispatch(clearAllCleanups());
                                      dispatch(setCurrentPageIsDirty(true));
                                    }}
                                  >
                                    {t("dataconsumption.clear")}
                                  </Button>
                                </ToolbarItem>
                                <ToolbarSeparator />
                                <ToolbarItem>
                                  <Button
                                    disabled={pageState.processing}
                                    style={{ color: currentTheme.primary }}
                                    icon="save"
                                    onClick={(e) => {
                                      if (
                                        formValidator(pageState.formValues) !==
                                        undefined
                                      )
                                        return;

                                      dispatch(setCleanups());
                                      dispatch(setCurrentPageIsDirty(true));
                                    }}
                                  >
                                    {t("dataconsumption.save")}
                                  </Button>
                                </ToolbarItem>
                                <ToolbarSeparator />
                              </Fragment>
                            )}
                          <ToolbarItem>
                            <Button
                              disabled={pageState.processing}
                              style={{ color: currentTheme.primary }}
                              icon="close"
                              onClick={onDialogClose}
                            >
                              {closeTitle}
                            </Button>
                          </ToolbarItem>
                        </Toolbar>
                      </div>
                      <div className="form-content">
                        {pageState.setRetentionPeriodDialogOpen && (
                          <Field
                            id={"RetentionPeriodInDays"}
                            name={"RetentionPeriodInDays"}
                            label={t("dataconsumption.retentionperiod")}
                            component={FormInput}
                            onChange={(e) =>
                              dispatch(
                                setFormValue({
                                  id: "RetentionPeriodInDays",
                                  value: e.value,
                                }),
                              )
                            }
                            autoFocus={true}
                            disabled={pageState.isProcessing}
                            style={{ width: "100%", flex: 1 }}
                            maxLength={50}
                            type={"number"}
                          />
                        )}
                        {pageState.databaseByMonthDialogOpen && (
                          <MyGrid
                            className="databasebymonth-table"
                            currentTheme={currentTheme}
                            schema={pageState.databaseByMonthTable.schema}
                            data={pageState.databaseByMonthTable.data}
                            isPageable={false}
                            isFilterable={false}
                            isSortable={true}
                          />
                        )}
                        {pageState.datasetByMonthDialogOpen && (
                          <MyGrid
                            className="datasetbymonth-table"
                            currentTheme={currentTheme}
                            schema={pageState.datasetByMonthTable.schema}
                            data={pageState.datasetByMonthTable.data}
                            isPageable={false}
                            isFilterable={false}
                            isSortable={true}
                          />
                        )}
                        {pageState.cleanupsDialogOpen && (
                          <div className="cleanups-form-content">
                            {pageState.formValues.Cleanups.map((e, index) => {
                              return (
                                <div className="row" key={index}>
                                  <div className="field">
                                    <Field
                                      id={`Cleanups[${index}].FieldId`}
                                      name={`Cleanups[${index}].FieldId`}
                                      label={t("dataconsumption.field")}
                                      component={FormDropDownListById}
                                      data={pageState.formValues.FieldsLookup}
                                      dataItemKey="id"
                                      textField="title"
                                      onChange={(function (outerIndex) {
                                        return function (ev) {
                                          let fieldId = ev.value;
                                          let dataset = pageState.datasets.find(
                                            (d) =>
                                              d.Id ==
                                              pageState.selectedDatasetId,
                                          );
                                          let fieldName = dataset.Fields.find(
                                            (f) => f.Id == fieldId,
                                          )?.Name;

                                          dispatch(
                                            setFormValue({
                                              id: "Cleanups",
                                              value:
                                                pageState.formValues.Cleanups.map(
                                                  (c, _index) => {
                                                    if (outerIndex !== _index)
                                                      return c;
                                                    else
                                                      return {
                                                        ...c,
                                                        FieldId: fieldId,
                                                        FieldName:
                                                          fieldName ?? "None",
                                                      };
                                                  },
                                                ),
                                            }),
                                          );
                                        };
                                      })(index)}
                                      disabled={pageState.processing}
                                      style={{ width: 240 }}
                                    />
                                  </div>
                                  <div className="operator">
                                    <Field
                                      id={`Cleanups[${index}].OperatorId`}
                                      name={`Cleanups[${index}].OperatorId`}
                                      label={t("dataconsumption.operator")}
                                      component={FormDropDownListById}
                                      data={
                                        pageState.formValues.OperatorsLookup
                                      }
                                      dataItemKey="id"
                                      textField="title"
                                      onChange={(function (outerIndex) {
                                        return function (ev) {
                                          let operatorId = ev.value;
                                          let operatorName = "None";

                                          if (operatorId == 1)
                                            operatorName = "=";
                                          else if (operatorId == 2)
                                            operatorName = "<>";
                                          else if (operatorId == 3)
                                            operatorName = "<";
                                          else if (operatorId == 4)
                                            operatorName = "<=";
                                          else if (operatorId == 5)
                                            operatorName = ">";
                                          else if (operatorId == 6)
                                            operatorName = ">=";

                                          dispatch(
                                            setFormValue({
                                              id: "Cleanups",
                                              value:
                                                pageState.formValues.Cleanups.map(
                                                  (c, _index) => {
                                                    if (outerIndex !== _index)
                                                      return c;
                                                    else
                                                      return {
                                                        ...c,
                                                        OperatorId: operatorId,
                                                        OperatorName:
                                                          operatorName,
                                                      };
                                                  },
                                                ),
                                            }),
                                          );
                                        };
                                      })(index)}
                                      disabled={pageState.processing}
                                    />
                                  </div>
                                  <div className="value">
                                    <Field
                                      id={`Cleanups[${index}].Value`}
                                      name={`Cleanups[${index}].Value`}
                                      label={t("dataconsumption.value")}
                                      component={FormInput}
                                      onChange={(function (outerIndex) {
                                        return function (ev) {
                                          let value = ev.value;

                                          dispatch(
                                            setFormValue({
                                              id: "Cleanups",
                                              value:
                                                pageState.formValues.Cleanups.map(
                                                  (c, _index) => {
                                                    if (outerIndex !== _index)
                                                      return c;
                                                    else
                                                      return {
                                                        ...c,
                                                        Value: value,
                                                      };
                                                  },
                                                ),
                                            }),
                                          );
                                        };
                                      })(index)}
                                      disabled={pageState.isProcessing}
                                      style={{}}
                                      maxLength={50}
                                      type={"number"}
                                    />
                                  </div>
                                  <div className="retentionperiod">
                                    <Field
                                      id={`Cleanups[${index}].RetentionPeriodInDays`}
                                      name={`Cleanups[${index}].RetentionPeriodInDays`}
                                      label={t("dataconsumption.retperiod")}
                                      component={FormInput}
                                      onChange={(function (outerIndex) {
                                        return function (ev) {
                                          let value = ev.value;

                                          dispatch(
                                            setFormValue({
                                              id: "Cleanups",
                                              value:
                                                pageState.formValues.Cleanups.map(
                                                  (c, _index) => {
                                                    if (outerIndex !== _index)
                                                      return c;
                                                    else
                                                      return {
                                                        ...c,
                                                        RetentionPeriodInDays:
                                                          value,
                                                      };
                                                  },
                                                ),
                                            }),
                                          );
                                        };
                                      })(index)}
                                      disabled={pageState.isProcessing}
                                      style={{}}
                                      maxLength={50}
                                      type={"number"}
                                    />
                                  </div>
                                  <div className="clear-strategy">
                                    <Button
                                      icon="delete"
                                      onClick={(function (outerIndex) {
                                        return function (ev) {
                                          dispatch(
                                            clearCleanupStrategy(outerIndex),
                                          );
                                        };
                                      })(index)}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </FormElement>
                  );
                }}
              />
            </Dialog>
          )}
        </div>
      )}
      <style>
        {`
                        .dataconsumption-page
                        {
                            height: auto;
                        }

                        .dataconsumption-page .dataconsumption-body
                        {
                            display: flex;
                            flex-direction: column;
                            min-width: 0;
                            padding: 16px;
                            box-sizing: border-box;
                        }

                   
                    .dataconsumption-body {
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                       
                    }

                        .dataconsumption-page .k-drawer
                        {
                            overflow: auto;
                        }

                        .dataconsumption-page .dataconsumption-body .page-header
                        {
                            display: flex;
                            font-weight: 700;
                            font-size: 14px;
                            margin-bottom: 12px;
                        }

                        .dataconsumption-page .dataconsumption-body .row
                        {
                            display: flex;
                            align-items: center;
                            justify-content: flex-start;
                            padding: 16px 2px;
                        }

                        .dataconsumption-page .dataconsumption-body .database
                        {
                            display: flex;
                            align-items: flex-start;
                            justify-content: flex-start;
                            min-width: 0;
                        }

                        .dataconsumption-page .dataconsumption-body .database .gauge
                        {
                            height: 170px;
                            width: 170px;
                        }

                        .dataconsumption-page .dataconsumption-body .database .summary
                        {
                            display: flex;
                            flex-direction: column;
                            align-items: flex-start;
                            justify-content: space-between;
                            padding: 8px 0;
                            margin-left: 0;
                            margin-inline-start: 50px;
                            padding-bottom: 20px;
                            height: 100%;
                            min-width: 0;
                        }

                        .dataconsumption-page .database-actions
                        {
                            display: flex;
                            flex-wrap: wrap;
                            gap: 8px;
                        }

                        .dataconsumption-page .database-actions .k-button + .k-button
                        {
                            margin-left: 0 !important;
                            margin-right: 0 !important;
                        }

                        .dataconsumption-page .storage-summary
                        {
                            display: flex;
                            flex-wrap: wrap;
                            gap: 12px 20px;
                            min-width: 0;
                        }

                        .dataconsumption-page .storage-summary .size-displayer
                        {
                            min-width: 112px;
                            padding-right: 12px;
                            padding-inline-end: 12px;
                        }

                        .dataconsumption-page .cumulative-cleaned-space
                        {
                        }

                        .dataconsumption-page .dataconsumption-body .database .operations
                        {
                            display: flex;
                            flex-direction: column;
                            align-items: flex-start;
                            justify-content: flex-end;
                            padding: 8px 0;
                            height: 100%;
                            margin-left: 50px;
                            padding-bottom: 30px;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets
                        {
                            margin-top: 14px;
                            width: 100%;
                            min-width: 0;
                            overflow-x: auto;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table
                        {
                            min-width: 960px;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(1),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(1)
                        {
                            width: 18%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(2),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(2)
                        {
                            width: 7%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(3),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(3)
                        {
                            width: 7%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(4),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(4)
                        {
                            width: 7%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(5),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(5)
                        {
                            width: 9%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(6),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(6)
                        {
                            width: 11%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(7),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(7)
                        {
                            width: 8%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(8),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(8)
                        {
                            width: 8%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(9),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(9)
                        {
                            width: 8%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(10),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(10)
                        {
                            width: 11%;
                        }

                        .dataconsumption-page .dataconsumption-body .datasets-table .k-grid-table col:nth-of-type(11),
                        .dataconsumption-page .dataconsumption-body .datasets-table col:nth-of-type(11)
                        {
                            width: 8%;
                        }

                        


                        .dataconsumption-page .dataconsumption-body .datasets-table .RowsCount,
                        .dataconsumption-page .dataconsumption-body .datasets-table .TotalSizeInMB ,
                        .dataconsumption-page .dataconsumption-body .datasets-table .Percentage ,
                        .dataconsumption-page .dataconsumption-body .datasets-table .ImportedRowsCount ,
                        .dataconsumption-page .dataconsumption-body .datasets-table .ImportedSizeInMB ,
                        .dataconsumption-page .dataconsumption-body .datasets-table .CleanedRowsCount ,
                        .dataconsumption-page .dataconsumption-body .datasets-table .CleanedSizeInMB ,
                        .dataconsumption-page .dataconsumption-body .datasets-table .RetentionPeriodInDays ,
                        .dataconsumption-page .dataconsumption-body .datasets-table .Cleanups ,
                        .dataconsumption-page .dataconsumption-body .datasets-table .Details 
                        {
                            text-align: center;
                            justify-content: center;
                        }


                        /*dialog*/
                        .dataconsumption-dialog
                        {
                            width: 1000px;
                            height: 500px;
                            left: calc((100% - 1000px)/2);
                            top: calc(50% - 250px);
                             direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                        }

                        .dataconsumption-dialog .k-dialog-content
                        {
                            padding: 0 !important;
                        }

                        .dataconsumption-dialog .k-dialog
                        {
                            width: ${
                              pageState.setRetentionPeriodDialogOpen ? 400 : 800
                            }px;
                            max-width: 1000px;
                            box-sizing: border-box;
                        }

                        .dataconsumption-dialog .k-form
                        {
                            width: 100%;
                            height: 100%;
                        }

                        .dataconsumption-dialog .k-form .form-content
                        {
                            height:450px;
                            overflow:auto;
                            padding: 12px 26px 28px 26px;
                        }

                        .dataconsumption-dialog .toolbar .k-button
                        {
                            border: none;
                            background-color:transparent;
                        }
                        
                        .dataconsumption-dialog .toolbar ,  .dataconsumption-dialog .k-toolbar
                        {
                            border-bottom: 1px solid ${
                              currentTheme.separatorColor
                            };
                             direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                        }

                        .dataconsumption-dialog .toolbar .k-separator 
                        {
                            border-color: ${
                              currentTheme.appBarLinkColor
                            } !important;
                        }

                        .databasebymonth-table .k-grid-table col:nth-of-type(1),
                        .databasebymonth-table col:nth-of-type(1),
                        .datasetbymonth-table .k-grid-table col:nth-of-type(1),
                        .datasetbymonth-table col:nth-of-type(1)
                        {
                            width: 10%;
                        }

                        .databasebymonth-table .k-grid-table col:nth-of-type(2),
                        .databasebymonth-table col:nth-of-type(2),
                        .datasetbymonth-table .k-grid-table col:nth-of-type(2),
                        .datasetbymonth-table col:nth-of-type(2)
                        {
                            width: 12%;
                        }

                        .databasebymonth-table .k-grid-table col:nth-of-type(3),
                        .databasebymonth-table col:nth-of-type(3),
                        .datasetbymonth-table .k-grid-table col:nth-of-type(3),
                        .datasetbymonth-table col:nth-of-type(3)
                        {
                            width: 20%;
                        }

                        .databasebymonth-table .k-grid-table col:nth-of-type(4),
                        .databasebymonth-table col:nth-of-type(4)
                        {
                            width: 20%;
                        }

                        .databasebymonth-table .k-grid-table col:nth-of-type(5),
                        .databasebymonth-table col:nth-of-type(5),
                        .datasetbymonth-table .k-grid-table col:nth-of-type(5),
                        .datasetbymonth-table col:nth-of-type(5)
                        {
                            width: 19%;
                        }

                        .databasebymonth-table .k-grid-table col:nth-of-type(6),
                        .databasebymonth-table col:nth-of-type(6),
                        .datasetbymonth-table .k-grid-table col:nth-of-type(6),
                        .datasetbymonth-table col:nth-of-type(6)
                        {
                            width: 19%;
                        }

                        .databasebymonth-table .Year,
                        .databasebymonth-table .Month ,
                        .databasebymonth-table .ImportedRowsCount ,
                        .databasebymonth-table .ImportedSizeInMB ,
                        .databasebymonth-table .CleanedRowsCount ,
                        .databasebymonth-table .CleanedSizeInMB,
                        .datasetbymonth-table .Year,
                        .datasetbymonth-table .Month ,
                        .datasetbymonth-table .ImportedRowsCount ,
                        .datasetbymonth-table .ImportedSizeInMB ,
                        .datasetbymonth-table .CleanedRowsCount ,
                        .datasetbymonth-table .CleanedSizeInMB 
                        {
                            text-align: center;
                            justify-content: center;
                        }


                        /*cleanup*/
                        .cleanups-form-content .row
                        {
                            display: flex;
                            align-items: flex-start;
                            justify-content: flex-start;
                            margin-top:16px;
                        }

                        .cleanups-form-content .row:nth-child(1)
                        {
                            margin-top: 0;
                        }

                        .cleanups-form-content .row .field
                        {
                            flex: 3;
                        }

                        .cleanups-form-content .row .operator
                        {
                            margin-left: 12px;
                            flex: 2;
                        }

                        .cleanups-form-content .row .value
                        {
                            margin-left: 12px;
                            flex: 2;
                        }

                        .cleanups-form-content .row .retentionperiod
                        {
                            margin-left: 12px;
                            flex: 2;
                        }

                        .cleanups-form-content .row .clear-strategy
                        {
                            margin-top: 34px;
                            margin-left: 12px;
                        }

                        @media (max-width: 1024px)
                        {
                            .dataconsumption-page > .content
                            {
                                overflow-y: auto;
                                overflow-x: hidden;
                            }

                            .dataconsumption-page .dataconsumption-body
                            {
                                min-height: 100%;
                                padding-bottom: calc(80px + env(safe-area-inset-bottom));
                            }

                            .dataconsumption-page .dataconsumption-body .database
                            {
                                align-items: center;
                            }

                            .dataconsumption-page .dataconsumption-body .datasets{
                                margin-bottom:100px;
                            }

                            .dataconsumption-page .dataconsumption-body .database .summary
                            {
                                flex: 1 1 0;
                            }

                            .dataconsumption-dialog
                            {
                                width: calc(100vw - 32px);
                                max-width: calc(100vw - 32px);
                                height: auto;
                                max-height: calc(100dvh - 32px);
                                left: 16px;
                                top: 16px;
                            }

                            .dataconsumption-dialog .k-dialog
                            {
                                width: 100%;
                                max-width: 100%;
                                max-height: calc(100dvh - 32px);
                            }

                            .dataconsumption-dialog .k-form .form-content
                            {
                                height: auto;
                                max-height: calc(100dvh - 112px);
                                padding: 12px 16px 20px;
                            }

                            .dataconsumption-cleanups-dialog .k-dialog
                            {
                                height: calc(100dvh - 32px - env(safe-area-inset-bottom));
                            }

                            .dataconsumption-cleanups-dialog .k-form
                            {
                                display: flex;
                                flex-direction: column;
                                min-height: 0;
                            }

                            .dataconsumption-cleanups-dialog .k-form .form-content
                            {
                                display: flex;
                                flex: 1 1 auto;
                                flex-direction: column;
                                min-height: 0;
                                height: auto;
                                max-height: none;
                                overflow: auto;
                                padding-bottom: calc(20px + env(safe-area-inset-bottom));
                            }

                            .dataconsumption-cleanups-dialog .cleanups-form-content .row
                            {
                                display: grid;
                                grid-template-columns: 1fr 1fr;
                                gap: 12px;
                                margin-top: 20px;
                            }

                            .dataconsumption-cleanups-dialog .cleanups-form-content .row .field,
                            .dataconsumption-cleanups-dialog .cleanups-form-content .row .operator,
                            .dataconsumption-cleanups-dialog .cleanups-form-content .row .value,
                            .dataconsumption-cleanups-dialog .cleanups-form-content .row .retentionperiod
                            {
                                margin-left: 0;
                                min-width: 0;
                            }

                            .dataconsumption-cleanups-dialog .cleanups-form-content .row .field,
                            .dataconsumption-cleanups-dialog .cleanups-form-content .row .clear-strategy
                            {
                                grid-column: 1 / -1;
                            }

                            .dataconsumption-cleanups-dialog .cleanups-form-content .row .clear-strategy
                            {
                                margin: 0;
                            }

                            .dataconsumption-cleanups-dialog .cleanups-form-content .k-form-field-wrap,
                            .dataconsumption-cleanups-dialog .cleanups-form-content .k-dropdownlist,
                            .dataconsumption-cleanups-dialog .cleanups-form-content .k-input
                            {
                                width: 100% !important;
                            }
                        }

                        @media (max-width: 767px)
                        {
                            .dataconsumption-page .dataconsumption-body
                            {
                                padding: 16px;
                            }

                            .dataconsumption-page .dataconsumption-body .database
                            {
                                flex-direction: column;
                                align-items: stretch;
                            }

                            .dataconsumption-page .dataconsumption-body .database .gauge
                            {
                                align-self: center;
                            }

                            .dataconsumption-page .dataconsumption-body .database .summary
                            {
                                width: 100%;
                                margin-inline-start: 0;
                                padding-bottom: 0;
                            }

                            .dataconsumption-page .database-actions
                            {
                                width: 100%;
                                margin-bottom:40px;
                            }

                            .dataconsumption-page .database-actions .k-button
                            {
                                flex: 1 1 130px;
                            }

                            .dataconsumption-page .storage-summary
                            {
                                width: 100%;
                                gap: 12px;
                            }

                            .dataconsumption-page .storage-summary .size-displayer
                            {
                                flex: 1 1 calc(50% - 6px);
                                min-width: 0;
                            }

                            .dataconsumption-page .dataconsumption-body .datasets-table
                            {
                                min-width: 880px;
                            }

                            .cleanups-form-content .row
                            {
                                display: grid;
                                grid-template-columns: 1fr 1fr;
                                gap: 12px;
                                margin-top: 20px;
                            }

                            .cleanups-form-content .row .field,
                            .cleanups-form-content .row .operator,
                            .cleanups-form-content .row .value,
                            .cleanups-form-content .row .retentionperiod
                            {
                                margin-left: 0;
                                min-width: 0;
                            }

                            .cleanups-form-content .row .field
                            {
                                grid-column: 1 / -1;
                            }

                            .cleanups-form-content .row .field .k-form-field-wrap,
                            .cleanups-form-content .row .operator .k-form-field-wrap,
                            .cleanups-form-content .row .value .k-form-field-wrap,
                            .cleanups-form-content .row .retentionperiod .k-form-field-wrap,
                            .cleanups-form-content .row .field .k-dropdownlist,
                            .cleanups-form-content .row .operator .k-dropdownlist,
                            .cleanups-form-content .row .value .k-input,
                            .cleanups-form-content .row .retentionperiod .k-input
                            {
                                width: 100% !important;
                            }

                            .cleanups-form-content .row .clear-strategy
                            {
                                grid-column: 1 / -1;
                                margin: 0;
                            }
                        }

                        @media (max-width: 480px)
                        {
                            // .dataconsumption-page .storage-summary .size-displayer
                            // {
                            //     flex-basis: 100%;
                            // }

                            .cleanups-form-content .row
                            {
                                grid-template-columns: 1fr;
                            }

                            .dataconsumption-cleanups-dialog .cleanups-form-content .row
                            {
                                grid-template-columns: 1fr;
                            }
                        }
                    `}
      </style>
    </>
  );
};
